import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import DocumentStatusBadge from "../../../components/common/alert/document-status-badge";
import UsersToolbar from "../../../components/common/card/users-toolbar";
import UserInitial from "../../../components/common/table/user-initial";
import {classNames} from "../../../utils/Utils";
import {getCustomFormat} from "../../../utils/timeUtils";
import {FALLBACK_VALUE, getDisplayValue, buildAddress} from "./document-header-utils";

const resolveCustomer = (document) => (
    document?.customer
    ?? document?.company_document?.connection
    ?? document?.customer_company
    ?? document?.company
    ?? null
);

const resolveCustomerName = (document) => {
    if (!document) {
        return null;
    }

    const customer = resolveCustomer(document);
    return customer?.business_name
        ?? document?.customer_name
        ?? customer?.company_name
        ?? customer?.name
        ?? document?.company_name
        ?? null;
};

const formatDate = (value) => {
    if (!value) {
        return FALLBACK_VALUE;
    }
    try {
        return getCustomFormat(value, "DD/MM/YYYY");
    } catch (error) {
        return FALLBACK_VALUE;
    }
};

const resolveDocumentNumber = (document) => (
    document?.document_reference
    ?? document?.internal_reference
    ?? document?.document_number
    ?? document?.code
    ?? null
);

const resolveAssignedUserName = (user) => {
    if (!user || typeof user !== "object") {
        return FALLBACK_VALUE;
    }

    const candidates = [
        user.fullname,
        [user.firstname, user.lastname, user.name].filter(Boolean).join(" "),
        user.identifier,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === "string") {
            const trimmed = candidate.trim();
            if (trimmed) {
                return trimmed;
            }
        }
    }

    return FALLBACK_VALUE;
};

const resolveAddressLabel = (address) => {
    if (!address) {
        return null;
    }

    const builtAddress = buildAddress(address);
    if (builtAddress) {
        return builtAddress;
    }

    const fallbackParts = [
        address.address,
        address.zip_code,
        address.city?.name,
        address.region?.name,
        address.country?.name,
    ].filter(Boolean);

    return fallbackParts.length > 0 ? fallbackParts.join(", ") : null;
};

const STATUS_GLOW_CLASSNAMES = {
    draft: "bg-slate-200",
    pending: "bg-amber-200",
    review: "bg-sky-200",
    confirmed: "bg-green-200",
    canceled: "bg-rose-200",
};

const resolveStatusGlowClass = (status) => {
    if (!status) {
        return "bg-blue-200";
    }

    const normalized = String(status).toLowerCase();
    return STATUS_GLOW_CLASSNAMES[normalized] ?? "bg-blue-200";
};

const ConnectionDocumentHeader = ({document}) => {
    const {t} = useTranslation();

    const customerName = useMemo(() => resolveCustomerName(document), [document]);

    const documentNumber = useMemo(() => resolveDocumentNumber(document), [document]);
    const rawDocumentDate = useMemo(() => (
        document?.document_date
        ?? document?.registration_date
        ?? null
    ), [document]);
    const formattedDocumentDate = useMemo(() => formatDate(rawDocumentDate), [rawDocumentDate]);
    const rawDeliveryDate = useMemo(() => document?.delivery_date ?? null, [document]);
    const formattedDeliveryDate = useMemo(() => formatDate(rawDeliveryDate), [rawDeliveryDate]);
    const statusGlowClass = useMemo(() => resolveStatusGlowClass(document?.status), [document?.status]);
    const deliveryAddressLabel = useMemo(() => resolveAddressLabel(document?.delivery_address), [document]);
    const connectionNote = useMemo(() => document?.connection_note, [document]);
    const connectionAddressLabel = useMemo(() => resolveAddressLabel(document?.connection_address), [document]);
    const companyPaymentTerms = useMemo(() => resolveAddressLabel(document?.company_payment_terms?.name ?? (document?.payment_terms ?? "-")), [document]);

    const assignedUsers = useMemo(() => (
        Array.isArray(document?.users) ? document.users : []
    ), [document]);
    const normalizedAssignedUsers = useMemo(() => (
        assignedUsers.map((user) => {
            const resolvedName = resolveAssignedUserName(user);
            if (user && typeof user === "object") {
                if (user.fullname) {
                    return user;
                }
                return {
                    ...user,
                    fullname: resolvedName,
                };
            }
            return {
                fullname: resolvedName,
            };
        })
    ), [assignedUsers]);
    const visibleAssignedUsers = useMemo(() => (
        normalizedAssignedUsers.length > 4
            ? normalizedAssignedUsers.slice(0, 4)
            : normalizedAssignedUsers
    ), [normalizedAssignedUsers]);
    const assignedUsersOverflowCount = Math.max(normalizedAssignedUsers.length - visibleAssignedUsers.length, 0);
    const singleAssignedUserName = normalizedAssignedUsers.length === 1 ? resolveAssignedUserName(normalizedAssignedUsers[0]) : null;

    if (!document) {
        return null;
    }

    return (
        <div className="space-y-6">
            <header className="relative isolate  border-b border-slate-200 bg-white px-4 py-8  sm:px-6 lg:px-8">
                <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden rounded-3xl">
                    <div className={classNames("absolute left-1/4 top-8 h-64 w-64 -translate-x-1/2 rounded-full opacity-40 blur-3xl sm:left-1/3", statusGlowClass)} />
                    <div className={classNames("absolute right-10 bottom-0 h-56 w-56 translate-y-1/2 rounded-full opacity-40 blur-3xl sm:right-16 sm:translate-y-1/3", statusGlowClass)} />
                </div>
                <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                    <div>
                        <p className="text-sm text-gray-500">
                            {t("connection.document.header.supplier", {defaultValue: "Fornitore"})}
                        </p>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {getDisplayValue(customerName)}
                        </h1>
                        <dl className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-600">
                            <div>
                                <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    {t("connection.document.header.document_number", {defaultValue: "Documento n°"})}
                                </dt>
                                <dd className="text-sm font-medium text-gray-900">
                                    {getDisplayValue(documentNumber)}
                                </dd>
                            </div>
                            <div>
                                <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                    {t("connection.document.header.document_date", {defaultValue: "Data documento"})}
                                </dt>
                                <dd className="text-sm font-medium text-gray-900">
                                    {formattedDocumentDate}
                                </dd>
                            </div>

                            {document?.status && (
                                <div>
                                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {t("document.form.status", {defaultValue: "Stato"})}
                                    </dt>
                                    <dd className="">
                                        <DocumentStatusBadge status={document.status} />
                                    </dd>
                                </div>
                            )}


                            {document?.delivery_date && (
                                <div>
                                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {t("connection.document.header.delivery_date", {defaultValue: "Data consegna"})}
                                    </dt>
                                    <dd className=" text-sm font-medium text-gray-900">
                                        {formattedDeliveryDate}
                                    </dd>
                                </div>
                            )}

                        </dl>


                        <dl className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-600">
                            {deliveryAddressLabel && (
                                <div>
                                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {t("connection.document.header.delivery_address", {defaultValue: "Indirizzo di consegna"})}
                                    </dt>
                                    <dd className="text-sm font-medium text-gray-900">
                                        {deliveryAddressLabel}
                                    </dd>
                                </div>
                            )}
                            {connectionAddressLabel && (
                                <div>
                                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {t("connection.document.header.destination_address", {defaultValue: "Indirizzo di destinazione"})}
                                    </dt>
                                    <dd className=" text-sm font-medium text-gray-900">
                                        {connectionAddressLabel}
                                    </dd>
                                </div>
                            )}
                        </dl>


                        <dl className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-600">
                            {companyPaymentTerms && (
                                <div>
                                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {t("connection.document.header.note", {defaultValue: "Termini di pagamento"})}
                                    </dt>
                                    <dd className="text-sm font-medium text-gray-900">
                                        {companyPaymentTerms}
                                    </dd>
                                </div>
                            )}

                            {connectionNote && (
                                <div>
                                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {t("connection.document.header.note", {defaultValue: "Note"})}
                                    </dt>
                                    <dd className=" text-sm font-medium text-gray-900">
                                        {connectionNote}
                                    </dd>
                                </div>
                            )}
                        </dl>

                    </div>
                    <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
                        <div>
                            <dd className="mt-1">
                                {normalizedAssignedUsers.length > 0 ? (
                                    <UsersToolbar
                                        users={normalizedAssignedUsers}
                                        isActive={normalizedAssignedUsers.length > 1}
                                        className="relative inline-flex"
                                    >
                                        <div className="flex cursor-pointer items-center gap-2">
                                            <div
                                                className={classNames(
                                                    "flex items-center",
                                                    normalizedAssignedUsers.length === 1 ? "gap-2 text-sm" : "-space-x-1"
                                                )}
                                            >
                                                {visibleAssignedUsers.map((user, index) => (
                                                    index === 0 ? (
                                                        <UserInitial
                                                            key={user?.id ?? index}
                                                            user={user}
                                                            ringClass=" ring-yellow-400 "
                                                        />
                                                    ) : (
                                                        <UserInitial
                                                            key={user?.id ?? index}
                                                            user={user}
                                                        />
                                                    )
                                                ))}
                                                {normalizedAssignedUsers.length === 1 && singleAssignedUserName && (
                                                    <span className="text-sm font-medium text-gray-900">
                                                            {singleAssignedUserName}
                                                        </span>
                                                )}
                                            </div>
                                            {assignedUsersOverflowCount > 0 && (
                                                <span className="flex-shrink-0 text-xs font-medium leading-5">
                                                        +{assignedUsersOverflowCount}
                                                    </span>
                                            )}
                                        </div>
                                    </UsersToolbar>
                                ) : (
                                    <span className="text-sm text-gray-500">
                                            {t("connection.document.header.no_users", {defaultValue: "Nessun utente assegnato"})}
                                        </span>
                                )}
                            </dd>
                        </div>
                    </div>
                </div>
            </header>
        </div>
    );
};

ConnectionDocumentHeader.propTypes = {
    document: PropTypes.object,
};

ConnectionDocumentHeader.defaultProps = {
    document: null,
};

export default ConnectionDocumentHeader;
